import { situational, situationKey, type Situation } from "./situation.js";
import type { Lang } from "./commentary.js";

/**
 * Talking between balls.
 *
 * A ball takes four seconds to describe and the next one is thirty seconds
 * away. Everything the app said was pinned to a delivery, so the sound came in
 * bursts with long silences between — which reads as broken rather than quiet,
 * because a real ground is never silent and a broadcast never is either.
 *
 * This fills the gap with what a commentator fills it with: where the game is.
 * The situation bank already existed and already knew how to read a chase, a
 * collapse, a partnership building — it was just only ever consulted at the
 * moment a ball was bowled, which is the one moment there is already something
 * to say.
 *
 * Two rules shape the whole thing.
 *
 * The next ball always wins. Filler is the lowest-priority sound in the app;
 * a delivery cancels it mid-word. Nobody should ever hear the score being
 * mused over while a wicket goes unremarked.
 *
 * Silence grows. The gaps widen as they go — a first remark soon after the
 * ball, then a longer wait, then longer again. A commentator who talks at a
 * constant rate into an empty over is a commentator nobody is listening to.
 */

/** How much the app talks when nothing is happening. */
export type Chatter = "off" | "occasional" | "lively" | "nonstop";

export const CHATTER: { v: Chatter; label: string; note: string }[] = [
  { v: "off", label: "Off", note: "Only when a ball is bowled." },
  { v: "occasional", label: "Occasional", note: "A remark if the wait gets long." },
  { v: "lively", label: "Lively", note: "Keeps the ground talking between balls." },
  { v: "nonstop", label: "Non-stop", note: "Barely draws breath. Best with a speaker." },
];

/**
 * When to speak after the last ball, in seconds.
 *
 * Widening on purpose, and capped in length rather than repeating the last gap
 * forever: past four or five remarks the over has plainly stalled and more
 * talking does not help.
 */
export function schedule(level: Chatter): number[] {
  switch (level) {
    case "off": return [];
    case "occasional": return [14, 34];
    case "lively": return [7, 17, 31, 49];
    case "nonstop": return [4, 10, 18, 28, 40, 54];
  }
}

/**
 * Pick a line that is not one of the last few.
 *
 * The situation rarely changes between two balls, so asking the bank twice in
 * an over tends to return the same sentence — and the same sentence twice in
 * thirty seconds sounds worse than saying nothing at all.
 */
export function chooseLine(
  s: Situation, lang: Lang, recent: string[], tries = 8,
): string | null {
  for (let i = 0; i < tries; i++) {
    const line = situational(s, lang);
    if (!line) return null;
    if (!recent.includes(line)) return line;
  }
  return null;
}

export interface FillerOptions {
  /** Say something. Filler is never urgent, so this should not interrupt. */
  speak: (text: string) => void;
  /** Where the game is right now, or null if there is nothing to describe. */
  situation: () => Situation | null;
  lang: () => Lang;
  level: () => Chatter;
}

/** How many past lines to avoid repeating. */
const MEMORY = 6;

export class Filler {
  private timers: ReturnType<typeof setTimeout>[] = [];
  private recent: string[] = [];
  private opts: FillerOptions;
  private running = false;
  /** The situation when the last ball landed, so a change can be noticed. */
  private lastKey: string | null = null;

  constructor(opts: FillerOptions) { this.opts = opts; }

  /** A ball has been bowled. Clear the queue and start the wait again. */
  onBall(): void {
    this.clear();
    this.lastKey = null;
    if (this.running) this.arm();
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.arm();
  }

  stop(): void {
    this.running = false;
    this.clear();
  }

  /** The chatter setting changed; re-arm against the new schedule. */
  refresh(): void {
    if (!this.running) return;
    this.clear();
    this.arm();
  }

  private clear(): void {
    for (const t of this.timers) clearTimeout(t);
    this.timers = [];
  }

  private arm(): void {
    const level = this.opts.level();
    if (level === "off") return;
    for (const at of schedule(level)) {
      this.timers.push(setTimeout(() => this.fire(), at * 1000));
    }
  }

  private fire(): void {
    if (!this.running) return;
    const s = this.opts.situation();
    if (!s) return;

    // A situation that has not changed since the last remark is not worth
    // remarking on again — say something only when the game has moved.
    const key = situationKey(s);
    if (key && key === this.lastKey) return;

    const line = chooseLine(s, this.opts.lang(), this.recent);
    if (!line) return;

    this.lastKey = key;
    this.recent.push(line);
    if (this.recent.length > MEMORY) this.recent.shift();
    this.opts.speak(line);
  }
}

/* ── the setting ────────────────────────────────────────────────────────── */

const KEY = "orbit.chatter";
let level: Chatter = "lively";
let watchers: ((c: Chatter) => void)[] = [];

try {
  const saved = localStorage.getItem(KEY);
  if (saved && CHATTER.some((c) => c.v === saved)) level = saved as Chatter;
} catch { /* private mode */ }

export function getChatter(): Chatter { return level; }

export function setChatter(c: Chatter): void {
  if (c === level) return;
  level = c;
  try { localStorage.setItem(KEY, c); } catch { /* private mode */ }
  for (const w of watchers) w(c);
}

/** Subscribe. Fires immediately so a control paints in the right position. */
export function onChatterChange(fn: (c: Chatter) => void): () => void {
  watchers.push(fn);
  fn(level);
  return () => { watchers = watchers.filter((w) => w !== fn); };
}
